import { forwardRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Flex, Icon, Image, Text, Tooltip } from '@chakra-ui/react';
import { FiChevronRight } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { fetchAccountFreshness } from '../../api/endpointsBackend';
import { formatDayShort, monthShort } from '../../utils/intlFormatters';
import { chartColor } from '../shared/TimeSeriesChart';
import StatusGlyph from '../shared/StatusGlyph';
import { cardStyle, cardTitle, viewAllStyle } from './dashboardStyles';

const GREEN = chartColor('redesign.accent.green');

// How a month is drawn: covered with movements, covered by a statement with
// none, partly covered, or not covered at all. A month with no statement is
// the one thing to fix, so it is the one drawn in red.
const CELL = {
  covered: { bg: GREEN },
  no_activity: { border: `2px solid ${GREEN}` },
  partial: {
    border: `1px solid ${GREEN}`,
    bg: `linear-gradient(to right, ${GREEN} 50%, transparent 50%)`,
  },

  missing: { border: '1px solid', borderColor: 'redesign.accent.red' },
};

// The status chip: a tint, and the status's own colour for the words.
const PILL = {
  up_to_date: { bg: 'rgba(21, 128, 61, 0.10)', color: 'redesign.amount.income', _dark: { bg: 'rgba(74, 222, 128, 0.14)' } },
  behind: { bg: 'rgba(180, 83, 9, 0.12)', color: 'redesign.text.warn', _dark: { bg: 'rgba(250, 204, 21, 0.14)' } },
  outdated: { bg: 'rgba(185, 28, 28, 0.08)', color: 'redesign.accent.red', _dark: { bg: 'rgba(248, 113, 113, 0.14)' } },
  none: { bg: 'redesign.bg.card', color: 'redesign.text.muted' },
};

const LEGEND = ['covered', 'no_activity', 'partial', 'missing'];

// Where the card is narrow the strip keeps the last six months: on phones,
// and at lg, where the dashboard grid puts it beside another card.
const NARROW_MONTHS = 6;
const CELL_SIZE = '12px';
// Room for the glyph and the longest status, Spanish "Desactualizada".
const PILL_WIDTH = '128px';

const monthIndex = (ym) => Number(ym.slice(5, 7)) - 1;

const narrowHidden = (i, total) => (
  i < total - NARROW_MONTHS
    ? { base: 'none', md: 'block', lg: 'none', xl: 'block' }
    : 'block'
);

// Forwards its ref so a Tooltip can anchor to it.
const Cell = forwardRef(function Cell({ state, current, ...rest }, ref) {
  return (
    <Box
      ref={ref}
      w={CELL_SIZE}
      h={CELL_SIZE}
      borderRadius="3px"
      flexShrink={0}
      {...CELL[state]}
      {...(current && { outline: '1px dashed', outlineColor: 'redesign.text.muted', outlineOffset: '1px' })}
      {...rest}
    />
  );
});

function AccountGlyph({ account }) {
  return (
    <Flex w="22px" h="22px" align="center" justify="center" flexShrink={0} fontSize="sm">
      {account.source_logo_url ? (
        <Image
          src={account.source_logo_url}
          alt=""
          boxSize="18px"
          objectFit="contain"
          fallback={<Text fontSize="sm">{account.icon || '🏦'}</Text>}
        />
      ) : (
        account.icon || '🏦'
      )}
    </Flex>
  );
}

/**
 * Whether each account's statements are up to date: the last twelve months of
 * statement coverage and a status. Each row opens that account's files.
 */
export default function DashboardStatementStatus() {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    let isCurrent = true;
    fetchAccountFreshness()
      .then((res) => { if (isCurrent) setData(res); })
      .catch(() => { if (isCurrent) setData({ months: [], accounts: [] }); });
    return () => { isCurrent = false; };
  }, []);

  const accounts = data?.accounts || [];
  const months = data?.months || [];
  const outdated = accounts.filter((a) => a.status === 'outdated').length;
  const behind = accounts.filter((a) => a.status === 'behind').length;
  const summary = [
    outdated > 0 && t(outdated === 1 ? 'statementStatus.outdatedOne' : 'statementStatus.outdatedOther', { count: outdated }),
    behind > 0 && t(behind === 1 ? 'statementStatus.behindOne' : 'statementStatus.behindOther', { count: behind }),
  ].filter(Boolean).join(' · ')
    || (accounts.length > 0 && accounts.every((a) => a.status === 'up_to_date')
      ? t('statementStatus.allUpToDate') : '');

  const detail = (account) => {
    if (!account.covered_through) return '';
    const through = t('statementStatus.through', { date: formatDayShort(account.covered_through) });
    if (!account.gaps) return through;
    const gaps = t(account.gaps === 1 ? 'statementStatus.gapsOne' : 'statementStatus.gapsOther', { count: account.gaps });
    return `${through} · ${gaps}`;
  };

  const cellLabel = (m) => {
    const label = `${monthShort(monthIndex(m.month))} ${m.month.slice(0, 4)} · ${t(`statementStatus.states.${m.state}`)}`;
    return m.current ? `${label} (${t('statementStatus.inProgress')})` : label;
  };

  return (
    <Box {...cardStyle}>
      <Flex justify="space-between" align="flex-start" mb={3} gap={2}>
        <Box minW={0}>
          <Text {...cardTitle}>{t('statementStatus.title')}</Text>
          {summary && (
            <Text fontSize="xs" color="redesign.text.muted" mt={1}>{summary}</Text>
          )}
        </Box>
        <Text {...viewAllStyle} onClick={() => navigate('/source-files')} flexShrink={0}>
          {t('navigation:sourceFiles')} <Icon as={FiChevronRight} w="14px" h="14px" />
        </Text>
      </Flex>

      {data === null && (
        <Text fontSize="sm" color="redesign.text.muted">{t('common:states.loading')}</Text>
      )}
      {data !== null && accounts.length === 0 && (
        <Text fontSize="sm" color="redesign.text.muted">{t('statementStatus.empty')}</Text>
      )}

      {accounts.length > 0 && (
        <>
          <Flex align="center" gap={2} mb={1} aria-hidden="true">
            <Box w="22px" flexShrink={0} />
            <Box flex={1} minW={0} />
            <Flex gap="4px" flexShrink={0}>
              {months.map((ym, i) => (
                <Text
                  key={ym}
                  w={CELL_SIZE}
                  textAlign="center"
                  fontSize="2xs"
                  color="redesign.text.muted"
                  display={narrowHidden(i, months.length)}
                >
                  {monthShort(monthIndex(ym)).charAt(0)}
                </Text>
              ))}
            </Flex>
            <Box w={PILL_WIDTH} flexShrink={0} />
          </Flex>

          <Flex direction="column" gap={1}>
            {accounts.map((account) => (
              <Flex
                key={account.account_id}
                align="center"
                gap={2}
                cursor="pointer"
                borderRadius="md"
                mx={-1}
                px={1}
                py="3px"
                _hover={{ bg: 'redesign.bg.hover' }}
                onClick={() => navigate(`/source-files?account_ids=${account.account_id}`)}
              >
                <AccountGlyph account={account} />
                <Box flex={1} minW={0}>
                  <Text fontSize="sm" color="redesign.text" isTruncated>{account.account_name}</Text>
                  <Text fontSize="2xs" color="redesign.text.muted" isTruncated>{detail(account)}</Text>
                </Box>
                <Flex gap="4px" flexShrink={0}>
                  {account.months.map((m, i) => (
                    <Tooltip key={m.month} label={cellLabel(m)} fontSize="xs" hasArrow>
                      <Cell
                        state={m.state}
                        current={m.current}
                        display={narrowHidden(i, account.months.length)}
                      />
                    </Tooltip>
                  ))}
                </Flex>
                <Flex w={PILL_WIDTH} justify="flex-end" flexShrink={0}>
                  <Flex
                    as="span"
                    align="center"
                    gap="5px"
                    minW={0}
                    h="22px"
                    pl="5px"
                    pr="8px"
                    borderRadius="6px"
                    {...PILL[account.status]}
                  >
                    <StatusGlyph status={account.status} />
                    <Text as="span" fontSize="xs" fontWeight={600} color="inherit" isTruncated>
                      {t(`statementStatus.status.${account.status}`)}
                    </Text>
                  </Flex>
                </Flex>
              </Flex>
            ))}
          </Flex>

          <Flex wrap="wrap" gap={3} mt={3} pt={3} borderTop="1px solid" borderColor="redesign.border">
            {LEGEND.map((state) => (
              <Flex key={state} align="center" gap={1.5}>
                <Cell state={state} />
                <Text fontSize="2xs" color="redesign.text.muted">{t(`statementStatus.states.${state}`)}</Text>
              </Flex>
            ))}
          </Flex>
        </>
      )}
    </Box>
  );
}
